import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Drawer, Image, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_FEEDBACK_API } from '../../api/user-feedback.js';
import { http } from '../../api/client.js';

type FeedbackStatus = 'open' | 'processing' | 'resolved';

interface FeedbackRow {
  feedback_id: string;
  title: string;
  body: string;
  images?: Array<{ name: string; dataUrl: string }>;
  page_url: string | null;
  status: FeedbackStatus;
  submitter_name: string;
  handler_note: string | null;
  createdon: string;
  modifiedon: string;
}

interface ListResp {
  rows: FeedbackRow[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_META: Record<FeedbackStatus, { text: string; color: string }> = {
  open: { text: '待处理', color: 'orange' },
  processing: { text: '处理中', color: 'blue' },
  resolved: { text: '已解决', color: 'green' },
};

function errorMessage(cause: unknown): string {
  if (cause && typeof cause === 'object' && 'response' in cause) {
    const response = (cause as { response?: { data?: { message?: string } } }).response;
    if (typeof response?.data?.message === 'string') return response.data.message;
  }
  return cause instanceof Error ? cause.message : '加载失败';
}

export function UserFeedbackPage() {
  const { t } = useTranslation("feedback");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailRequest = useRef(0);
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<FeedbackStatus | undefined>();
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<FeedbackRow | null>(null);
  const [nextStatus, setNextStatus] = useState<FeedbackStatus>('open');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    http
      .get<ListResp>('/user-feedback', {
        ...USER_FEEDBACK_API,
        params: { page, pageSize: 20, status, q: keyword || undefined },
      })
      .then((res) => setData(res.data))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setLoading(false));
  }, [keyword, page, status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openRow = (row: FeedbackRow) => {
    const request = ++detailRequest.current;
    setSelected(row); setDetailLoading(true); setDetailError(null);
    http.get<FeedbackRow>(`/user-feedback/${row.feedback_id}`, USER_FEEDBACK_API)
      .then(({ data }) => { if (request === detailRequest.current) setSelected(data); })
      .catch(() => { if (request === detailRequest.current) setDetailError(t('detailFailed')); })
      .finally(() => { if (request === detailRequest.current) setDetailLoading(false); });
    setNextStatus(row.status);
    setNote(row.handler_note ?? '');
    setSaveError(null);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      await http.patch(`/user-feedback/${selected.feedback_id}`, {
        status: nextStatus,
        handlerNote: note,
      }, USER_FEEDBACK_API);
      setSelected(null);
      refresh();
    } catch (cause) {
      setSaveError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<FeedbackRow> = [
    {
      title: '时间',
      dataIndex: 'createdon',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: FeedbackStatus) => <Tag color={STATUS_META[value].color}>{STATUS_META[value].text}</Tag>,
    },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '反馈人', dataIndex: 'submitter_name', width: 140 },
    {
      title: '页面',
      dataIndex: 'page_url',
      width: 240,
      ellipsis: true,
      render: (value: string | null) => value ?? '—',
    },
  ];

  return (
    <Card
      title="用户反馈"
      extra={
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          刷新
        </Button>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder="搜索标题、描述或反馈人"
          style={{ width: 280 }}
          onSearch={(value) => {
            setPage(1);
            setKeyword(value.trim());
          }}
        />
        <Select
          allowClear
          placeholder="全部状态"
          style={{ width: 140 }}
          value={status}
          onChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
          options={(Object.keys(STATUS_META) as FeedbackStatus[]).map((key) => ({
            value: key,
            label: STATUS_META[key].text,
          }))}
        />
      </Space>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      <Table
        rowKey="feedback_id"
        loading={loading}
        columns={columns}
        dataSource={data?.rows ?? []}
        pagination={{
          current: page,
          pageSize: 20,
          total: data?.total ?? 0,
          onChange: setPage,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onRow={(row) => ({ onClick: () => openRow(row), style: { cursor: 'pointer' } })}
      />
      <Drawer
        title={selected?.title ?? '反馈详情'}
        open={Boolean(selected)}
        width={480}
        onClose={() => { detailRequest.current++; setSelected(null); }}
        extra={
          <Button type="primary" loading={saving} disabled={detailLoading || Boolean(detailError)} onClick={() => void save()}>
            保存
          </Button>
        }
      >
        {selected ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {detailError && <Alert type="error" showIcon message={detailError} />}
            {saveError ? <Alert type="error" showIcon message={saveError} /> : null}
            <div>
              <Typography.Text type="secondary">反馈人</Typography.Text>
              <div>{selected.submitter_name}</div>
            </div>
            <div>
              <Typography.Text type="secondary">页面</Typography.Text>
              <div>{selected.page_url ?? '—'}</div>
            </div>
            <div>
              <Typography.Text type="secondary">问题描述</Typography.Text>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {selected.body}
              </Typography.Paragraph>
              <Image.PreviewGroup><Space wrap style={{ marginTop: 12 }}>
                {selected.images?.map((image, index) => <Image key={`${image.name}-${index}`} src={image.dataUrl}
                  alt={image.name} width={120} height={90} style={{ objectFit: 'contain' }} />)}
              </Space></Image.PreviewGroup>
            </div>
            <div>
              <Typography.Text type="secondary">处理状态</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={nextStatus}
                onChange={setNextStatus}
                options={(Object.keys(STATUS_META) as FeedbackStatus[]).map((key) => ({
                  value: key,
                  label: STATUS_META[key].text,
                }))}
              />
            </div>
            <div>
              <Typography.Text type="secondary">处理说明</Typography.Text>
              <Input.TextArea
                style={{ marginTop: 8 }}
                rows={4}
                maxLength={2000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </Space>
        ) : null}
      </Drawer>
    </Card>
  );
}
